"use client";

import { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";

export function useResource<T>(path: string | null, interval = 0, enabled = true) {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    path: string | null;
    data?: T;
    error?: string;
    loading: boolean;
  }>({ path: null, loading: true });
  useEffect(() => {
    if (!path || !enabled) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function load() {
      try {
        const data = await api<T>(path!, { signal: controller.signal });
        if (!controller.signal.aborted)
          setState({ path, data, loading: false });
      } catch (error) {
        if (!controller.signal.aborted)
          setState((old) => ({
            path,
            data: old.path === path ? old.data : undefined,
            error: errorMessage(error),
            loading: false,
          }));
      }
      if (interval && !controller.signal.aborted)
        timer = setTimeout(load, interval);
    }
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [path, interval, revision, enabled]);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  return {
    data: state.path === path ? state.data : undefined,
    error: state.path === path ? state.error : undefined,
    loading: !!path && enabled && (state.path !== path || state.loading),
    refresh,
  };
}
